/**
 * The saved-search bookmark: a ribbon that comes from behind the card, folds
 * over its top edge and hangs down the front. Removing it pulls it up and over
 * the edge; adding it feeds it back out.
 *
 * The artwork (body and 39 stitches) stays intact in PATHS, and every vertex
 * goes through a small vertical warp each frame:
 *
 *   y' = y + lerp(topY, tipY, s)           s = 0 at the fold, 1 at the tip
 *   x' = cx + (x - cx) * (1 - narrow * s)  slight narrowing when taut
 *
 * Everything above the card edge is clipped, which is "behind the card" in 2D.
 * A requestAnimationFrame loop runs only while something moves. Plain DOM, no
 * React: mount it in an absolutely positioned 29 by 38 button whose top sits
 * 1px above the card's outer top edge.
 */

const BLUE = '#0040E6';
const WHITE = 'white';

/** Vertices of the 29 by 38 artwork: [d, fill, fillOpacity]. Entry 0 is the body. */
const PATHS: ReadonlyArray<readonly [string, string, number]> = [
  ['M4 1H25V31L14.5 23.1053L4 31V1Z', BLUE, 1],
  ['M5.23242 27.4854H5.5V27.626L5 28V27.375L5.09668 27.3037L5.23242 27.4854Z', WHITE, 0.3],
  ['M24 27.375V28L23.5 27.626V27.4854H23.7676L23.9033 27.3037L24 27.375Z', WHITE, 0.3],
  ['M6.97852 26.5186L6.18652 27.1113L5.8877 26.7119L6.67969 26.1191L6.97852 26.5186Z', WHITE, 0.3],
  ['M23.1123 26.7119L22.8125 27.1113L22.0205 26.5186L22.3203 26.1191L23.1123 26.7119Z', WHITE, 0.3],
  ['M5.5 26.4561H5V25.4268H5.5V26.4561Z', WHITE, 0.3],
  ['M24 26.4561H23.5V25.4268H24V26.4561Z', WHITE, 0.3],
  [
    'M8.41211 25.1357L8.56152 25.335L7.77051 25.9277L7.47168 25.5273L8.2627 24.9355L8.41211 25.1357Z',
    WHITE,
    0.3,
  ],
  ['M21.5283 25.5273L21.2285 25.9277L20.4375 25.335L20.7373 24.9355L21.5283 25.5273Z', WHITE, 0.3],
  ['M10.1455 24.1514L9.35352 24.7422L9.05469 24.3428L9.84668 23.751L10.1455 24.1514Z', WHITE, 0.3],
  [
    'M19.9453 24.3428L19.6455 24.7422L18.8535 24.1514L19.0039 23.9512L19.1533 23.751L19.9453 24.3428Z',
    WHITE,
    0.3,
  ],
  ['M5.5 24.3975H5V23.3672H5.5V24.3975Z', WHITE, 0.3],
  ['M24 24.3975H23.5V23.3672H24V24.3975Z', WHITE, 0.3],
  ['M11.7285 22.9658L10.9365 23.5586L10.6377 23.1592L11.4297 22.5664L11.7285 22.9658Z', WHITE, 0.3],
  ['M18.3623 23.1592L18.0625 23.5586L17.2705 22.9658L17.5703 22.5664L18.3623 23.1592Z', WHITE, 0.3],
  [
    'M13.1621 21.583L13.3115 21.7822L12.5205 22.375L12.2217 21.9746L13.0127 21.3828L13.1621 21.583Z',
    WHITE,
    0.3,
  ],
  ['M16.7783 21.9746L16.4785 22.375L15.6875 21.7822L15.9873 21.3828L16.7783 21.9746Z', WHITE, 0.3],
  ['M5.5 22.3379H5V21.3086H5.5V22.3379Z', WHITE, 0.3],
  ['M24 22.3379H23.5V21.3086H24V22.3379Z', WHITE, 0.3],
  [
    'M14.7998 20.4941L15.1953 20.79L15.0449 20.9893L15.0459 20.9902L14.8955 21.1895L14.5 20.8945L14.1035 21.1895L13.9541 20.9902L13.8047 20.79L14.2002 20.4941L14.5 20.2705L14.7998 20.4941Z',
    WHITE,
    0.3,
  ],
  ['M5.5 20.2793H5V19.25H5.5V20.2793Z', WHITE, 0.3],
  ['M24 20.2793H23.5V19.25H24V20.2793Z', WHITE, 0.3],
  ['M5.5 18.2207H5V17.1914H5.5V18.2207Z', WHITE, 0.3],
  ['M24 18.2207H23.5V17.1914H24V18.2207Z', WHITE, 0.3],
  ['M5.5 16.1621H5V15.1328H5.5V16.1621Z', WHITE, 0.3],
  ['M24 16.1621H23.5V15.1328H24V16.1621Z', WHITE, 0.3],
  ['M5.5 14.1025H5V13.0732H5.5V14.1025Z', WHITE, 0.3],
  ['M24 14.1025H23.5V13.0732H24V14.1025Z', WHITE, 0.3],
  ['M5.5 12.0439H5V11.0146H5.5V12.0439Z', WHITE, 0.3],
  ['M24 12.0439H23.5V11.0146H24V12.0439Z', WHITE, 0.3],
  ['M5.5 9.98535H5V8.95605H5.5V9.98535Z', WHITE, 0.3],
  ['M24 9.98535H23.5V8.95605H24V9.98535Z', WHITE, 0.3],
  ['M5.5 7.92676H5V6.89746H5.5V7.92676Z', WHITE, 0.3],
  ['M24 7.92676H23.5V6.89746H24V7.92676Z', WHITE, 0.3],
  ['M5.5 5.86719H5V4.83789H5.5V5.86719Z', WHITE, 0.3],
  ['M24 5.86719H23.5V4.83789H24V5.86719Z', WHITE, 0.3],
  ['M5.5 3.80859H5V2.7793H5.5V3.80859Z', WHITE, 0.3],
  ['M24 3.80859H23.5V2.7793H24V3.80859Z', WHITE, 0.3],
  ['M5.5 1.75H5V1H5.5V1.75Z', WHITE, 0.3],
  ['M24 1.75H23.5V1H24V1.75Z', WHITE, 0.3],
];

/** The y of the flat top edge: the fold over the card edge. */
const TOP = 1;
/** Fold to tip. */
const HEIGHT = 30;
/** Horizontal center. */
const CX = 14.5;
const VIEW_W = 29;
const VIEW_H = 38;
const NS = 'http://www.w3.org/2000/svg';

type Ease = readonly [number, number, number, number];
type SpringMotion = {
  type: 'spring';
  visualDuration?: number;
  bounce?: number;
  stiffness?: number;
  damping?: number;
  mass?: number;
};
type EasingMotion = { type: 'easing'; duration?: number; ease?: Ease };

export type BookmarkParams = {
  hover: {
    /** Pixels the ribbon is pulled up behind the edge; the visible part shortens. */
    rise: number;
    /** Extra drop shadow offset; 0 keeps it pressed to the card. */
    shadowLift: number;
    motion: SpringMotion | EasingMotion;
  };
  remove: {
    /** Pixels fed out first, for anticipation, before the pull. */
    tug: number;
    tugDuration: number;
    pull: EasingMotion;
    /** Milliseconds the tip trails the top, taking up slack. */
    tipLag: number;
    /** Narrowing at the tip at full stretch, from 0 to 1. */
    narrow: number;
    /** Pixels of top to tip separation that count as full stretch. */
    stretchRef: number;
  };
  add: {
    feed: EasingMotion;
    /** Milliseconds the tip leads the body as it comes out. */
    tipLead: number;
    /** Pixels the tip drops past rest before it settles back. */
    settle: number;
  };
  slot: {
    /** Pixels above the resting top edge where the clip sits; 0 is the card edge. */
    offset: number;
  };
};

const DEFAULTS: BookmarkParams = {
  hover: {
    rise: 3,
    shadowLift: 0,
    motion: { type: 'spring', visualDuration: 0.26, bounce: 0.2 },
  },
  remove: {
    tug: 2,
    tugDuration: 90,
    pull: { type: 'easing', duration: 0.34, ease: [0.5, 0, 0.55, 1] },
    tipLag: 70,
    narrow: 0.08,
    stretchRef: 8,
  },
  add: {
    feed: { type: 'easing', duration: 0.42, ease: [0.2, 0.7, 0.2, 1] },
    tipLead: 50,
    settle: 2,
  },
  slot: {
    offset: 0,
  },
};

type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] };

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const lerp = (from: number, to: number, t: number) => from + (to - from) * t;

/** Cubic bezier easing with the semantics of CSS `cubic-bezier`. */
const cubicBezier = (x1: number, y1: number, x2: number, y2: number) => {
  const ax = 1 - 3 * x2 + 3 * x1;
  const bx = 3 * x2 - 6 * x1;
  const cx = 3 * x1;
  const ay = 1 - 3 * y2 + 3 * y1;
  const by = 3 * y2 - 6 * y1;
  const cy = 3 * y1;
  const sx = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sy = (t: number) => ((ay * t + by) * t + cy) * t;
  const dx = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  return (x: number) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const d = dx(t);
      if (Math.abs(d) < 1e-6) break;
      const e = sx(t) - x;
      if (Math.abs(e) < 1e-6) return sy(t);
      t -= e / d;
    }
    let lo = 0;
    let hi = 1;
    t = x;
    while (hi - lo > 1e-6) {
      if (sx(t) < x) lo = t;
      else hi = t;
      t = (lo + hi) / 2;
    }
    return sy(t);
  };
};

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;
/** A 0 to 1 to 0 bump that peaks late, near u = 0.75; the add "settle". */
const lateBump = (u: number) => (u * u * u * (1 - u)) / 0.10546875;

const easeCache = new Map<string, (x: number) => number>();
const easeFor = (motion: EasingMotion | undefined, fallback: Ease) => {
  const ease = motion?.ease ?? fallback;
  const key = ease.join(',');
  let curve = easeCache.get(key);
  if (!curve) {
    curve = cubicBezier(...ease);
    easeCache.set(key, curve);
  }
  return curve;
};

/** Milliseconds of an easing, or of a spring's visual duration. */
const msOf = (motion: EasingMotion | undefined, fallbackSeconds: number) =>
  (motion?.duration ?? fallbackSeconds) * 1000;

/** Turns a { visualDuration, bounce } spring into stiffness, damping and mass. */
const springParams = (motion: SpringMotion) => {
  let { stiffness, damping } = motion;
  const mass = motion.mass ?? 1;
  if (motion.visualDuration != null || stiffness == null) {
    const visualDuration = motion.visualDuration ?? 0.3;
    const bounce = motion.bounce ?? 0.25;
    const root = (2 * Math.PI) / (visualDuration * 1.2);
    stiffness = root * root;
    damping = 2 * clamp(1 - bounce, 0.05, 1) * Math.sqrt(stiffness * mass);
  }
  return { stiffness, damping: damping ?? 10, mass };
};

const isPlain = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

/** Deep merge in which an override with a `type` (a motion) replaces the default whole. */
const mergeParams = <T extends Record<string, unknown>>(base: T, over?: DeepPartial<T>): T => {
  const out: Record<string, unknown> = {};
  const overrides = (over ?? {}) as Record<string, unknown>;
  for (const key of new Set([...Object.keys(base), ...Object.keys(overrides)])) {
    const baseValue = base[key];
    const overValue = overrides[key];
    if (overValue === undefined) out[key] = baseValue;
    else if (isPlain(baseValue) && isPlain(overValue) && !('type' in overValue)) {
      out[key] = mergeParams(baseValue, overValue);
    } else out[key] = overValue;
  }
  return out as T;
};

type Point = [number, number];

/** Polygons of a path made of absolute M, L, H, V and Z commands only, as the artwork is. */
const parsePolys = (d: string): Point[][] => {
  const tokens = d.match(/[MLHVZ]|-?\d*\.?\d+/g) ?? [];
  const polys: Point[][] = [];
  let poly: Point[] | null = null;
  let command: string | null = null;
  let x = 0;
  let y = 0;
  let index = 0;
  const num = () => parseFloat(tokens[index++] ?? '0');
  while (index < tokens.length) {
    const token = tokens[index] ?? '';
    if (/[MLHVZ]/.test(token)) {
      command = token;
      index++;
      if (command === 'Z') {
        poly = null;
        continue;
      }
    }
    switch (command) {
      case 'M':
        x = num();
        y = num();
        poly = [];
        polys.push(poly);
        poly.push([x, y]);
        command = 'L';
        break;
      case 'L':
        x = num();
        y = num();
        poly?.push([x, y]);
        break;
      case 'H':
        x = num();
        poly?.push([x, y]);
        break;
      case 'V':
        y = num();
        poly?.push([x, y]);
        break;
      default:
        index++;
    }
  }
  return polys;
};

type BookmarkPhase = 'idle' | 'removing' | 'removed' | 'adding';

export type Bookmark = {
  svg: SVGSVGElement;
  /** Grabs the ribbon while the pointer or focus is on it. */
  setHover: (on: boolean) => void;
  /** Pulls the ribbon up behind the card. Resolves true once it is hidden. */
  remove: () => Promise<boolean>;
  /** Feeds the ribbon out from behind the card. Resolves true once it settles. */
  add: () => Promise<boolean>;
  /** Snaps back to the resting state. */
  reset: () => void;
  getPhase: () => BookmarkPhase;
  destroy: () => void;
};

/** Mounts the bookmark in `host`. */
export const createBookmark = (
  host: HTMLElement,
  options: { params?: DeepPartial<BookmarkParams>; startRemoved?: boolean } = {},
): Bookmark => {
  const params = mergeParams(DEFAULTS, options.params);

  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', `0 0 ${VIEW_W} ${VIEW_H}`);
  svg.setAttribute('width', String(VIEW_W));
  svg.setAttribute('height', String(VIEW_H));
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');
  svg.style.overflow = 'visible';
  svg.style.display = 'block';

  const uid = Math.random().toString(36).slice(2, 8);
  const filterId = `bm-shadow-${uid}`;
  const clipId = `bm-clip-${uid}`;

  // The original drop shadow and two inner shadows, with a much larger region
  // so nothing gets cropped while the shape moves.
  const defs = document.createElementNS(NS, 'defs');
  defs.innerHTML = `
    <filter id="${filterId}" x="-30" y="-60" width="90" height="160" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="bg"/>
      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
      <feOffset dy="3" data-role="drop-offset"/>
      <feGaussianBlur stdDeviation="2" data-role="drop-blur"/>
      <feComposite in2="hardAlpha" operator="out"/>
      <feColorMatrix type="matrix" values="0 0 0 0 0.00784314 0 0 0 0 0.054902 0 0 0 0 0.376471 0 0 0 0.25 0"/>
      <feBlend mode="normal" in2="bg" result="drop"/>
      <feBlend mode="normal" in="SourceGraphic" in2="drop" result="shape"/>
      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
      <feOffset dy="12"/>
      <feGaussianBlur stdDeviation="2"/>
      <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
      <feColorMatrix type="matrix" values="0 0 0 0 0.0392157 0 0 0 0 0.0705882 0 0 0 0 0.643137 0 0 0 0.3 0"/>
      <feBlend mode="normal" in2="shape" result="inner1"/>
      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
      <feOffset dy="4"/>
      <feGaussianBlur stdDeviation="2"/>
      <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
      <feColorMatrix type="matrix" values="0 0 0 0 0.976471 0 0 0 0 0.988235 0 0 0 0 1 0 0 0 0.4 0"/>
      <feBlend mode="normal" in2="inner1" result="inner2"/>
    </filter>
    <clipPath id="${clipId}"><rect x="-40" y="${TOP}" width="120" height="400"/></clipPath>`;
  svg.appendChild(defs);

  const dropOffset = defs.querySelector('[data-role="drop-offset"]');
  const dropBlur = defs.querySelector('[data-role="drop-blur"]');
  const clipRect = defs.querySelector(`#${clipId} rect`);

  // Clipped at the card edge.
  const root = document.createElementNS(NS, 'g');
  root.setAttribute('clip-path', `url(#${clipId})`);
  const bodyGroup = document.createElementNS(NS, 'g');
  bodyGroup.setAttribute('filter', `url(#${filterId})`);
  const stitchGroup = document.createElementNS(NS, 'g');
  root.appendChild(bodyGroup);
  root.appendChild(stitchGroup);
  svg.appendChild(root);

  // Parse every path into polygons once; each frame writes a warped `d`.
  const shapes = PATHS.map(([d, fill, opacity], index) => {
    const element = document.createElementNS(NS, 'path');
    element.style.fill = fill;
    if (opacity !== 1) element.setAttribute('fill-opacity', String(opacity));
    (index === 0 ? bodyGroup : stitchGroup).appendChild(element);
    return { element, polys: parsePolys(d) };
  });

  host.appendChild(svg);

  let hoverTarget = 0;
  // Hover amount and its velocity, for the spring.
  let hover = 0;
  let hoverVelocity = 0;
  // Start of an easing hover, when the motion is an easing.
  let hoverFrom = 0;
  let hoverStart = 0;
  let phase: BookmarkPhase = options.startRemoved ? 'removed' : 'idle';
  let phaseStart = 0;
  let frameId = 0;
  let last = 0;
  let clock = 0;
  let onRemoved: (() => void) | null = null;
  let onAdded: (() => void) | null = null;

  // How far up the ribbon must go to be completely behind the card.
  const exitDistance = () => HEIGHT + params.slot.offset + 3;
  const pullMs = () => msOf(params.remove.pull, 0.34);
  const feedMs = () => msOf(params.add.feed, 0.42);
  const removeTotal = () => params.remove.tugDuration + pullMs() + params.remove.tipLag;
  const addTotal = () => feedMs() + params.add.tipLead;

  /** Vertical offset of the top edge, t ms into the remove sequence. */
  const removeCurve = (t: number) => {
    const { tug, tugDuration } = params.remove;
    if (t <= 0) return 0;
    if (t < tugDuration) return tug * easeOutCubic(t / tugDuration);
    const u = clamp((t - tugDuration) / pullMs(), 0, 1);
    const ease = easeFor(params.remove.pull, DEFAULTS.remove.pull.ease ?? [0, 0, 1, 1]);
    return lerp(tug, -exitDistance(), ease(u));
  };

  /** Vertical offset of the tip, t ms into the add sequence, which starts hidden. */
  const addCurve = (t: number) => {
    if (t <= 0) return -exitDistance();
    const u = clamp(t / feedMs(), 0, 1);
    const ease = easeFor(params.add.feed, DEFAULTS.add.feed.ease ?? [0, 0, 1, 1]);
    return -exitDistance() * (1 - ease(u)) + params.add.settle * lateBump(u);
  };

  const currentPose = () => {
    let topY = 0;
    let tipY = 0;
    if (phase === 'removing') {
      const t = clock - phaseStart;
      topY = removeCurve(t);
      // The tip takes up slack first.
      tipY = removeCurve(t - params.remove.tipLag);
    } else if (phase === 'adding') {
      const t = clock - phaseStart;
      tipY = addCurve(t);
      // The body follows the tip out.
      topY = addCurve(t - params.add.tipLead);
    } else if (phase === 'removed') {
      topY = -exitDistance();
      tipY = topY;
    }
    const rise = -params.hover.rise * hover;
    topY += rise;
    tipY += rise;
    // The ribbon continues behind the card, so its visible top edge never
    // drops below the fold. A downward overshoot (the add settle, the click
    // tug) becomes slack: the tip moves, the fold stays pinned and the body
    // stretches. Otherwise the card shows through a 1 to 2px gap.
    topY = Math.min(topY, -params.slot.offset);
    tipY = Math.max(tipY, topY);
    // Positive when pulled taut.
    const stretch = clamp((tipY - topY) / params.remove.stretchRef, -1, 1);
    return { topY, tipY, narrow: params.remove.narrow * Math.max(stretch, 0) };
  };

  const warpPoint = (
    x: number,
    y: number,
    pose: ReturnType<typeof currentPose>,
  ): [number, number] => {
    const s = clamp((y - TOP) / HEIGHT, 0, 1);
    return [CX + (x - CX) * (1 - pose.narrow * s), y + lerp(pose.topY, pose.tipY, s)];
  };

  const render = () => {
    const pose = currentPose();
    for (const { element, polys } of shapes) {
      let d = '';
      for (const poly of polys) {
        poly.forEach(([px, py], index) => {
          const [x, y] = warpPoint(px, py, pose);
          d += `${index === 0 ? 'M' : 'L'}${x.toFixed(3)} ${y.toFixed(3)}`;
        });
        d += 'Z';
      }
      element.setAttribute('d', d);
    }
    dropOffset?.setAttribute('dy', (3 + params.hover.shadowLift * hover).toFixed(3));
    dropBlur?.setAttribute('stdDeviation', (2 + params.hover.shadowLift * 0.5 * hover).toFixed(3));
    clipRect?.setAttribute('y', String(TOP - params.slot.offset));
    svg.style.visibility = phase === 'removed' ? 'hidden' : 'visible';
  };

  /** Advances the hover motion; true once it rests. */
  const stepHover = (dt: number) => {
    const motion = params.hover.motion;
    if (motion.type === 'easing') {
      const duration = (motion.duration ?? 0.2) * 1000;
      const u = duration <= 0 ? 1 : clamp((clock - hoverStart) / duration, 0, 1);
      const ease = easeFor(motion, [0.25, 0.1, 0.25, 1]);
      hover = lerp(hoverFrom, hoverTarget, ease(u));
      hoverVelocity = 0;
      return u >= 1;
    }
    const { stiffness, damping, mass } = springParams(motion);
    const seconds = dt / 1000;
    const acceleration = (-stiffness * (hover - hoverTarget) - damping * hoverVelocity) / mass;
    hoverVelocity += acceleration * seconds;
    hover += hoverVelocity * seconds;
    if (Math.abs(hoverVelocity) < 0.001 && Math.abs(hover - hoverTarget) < 0.001) {
      hover = hoverTarget;
      hoverVelocity = 0;
      return true;
    }
    return false;
  };

  // The frame loop runs only while something moves.
  const frame = (now: number) => {
    frameId = 0;
    const dt = Math.min(32, last ? now - last : 16);
    last = now;
    clock += dt;

    const hoverDone = stepHover(dt);
    let phaseDone = true;
    if (phase === 'removing' || phase === 'adding') {
      const elapsed = clock - phaseStart;
      const total = phase === 'removing' ? removeTotal() : addTotal();
      if (elapsed >= total) {
        if (phase === 'removing') {
          phase = 'removed';
          hoverTarget = 0;
          hover = 0;
          hoverVelocity = 0;
          const done = onRemoved;
          onRemoved = null;
          done?.();
        } else {
          phase = 'idle';
          const done = onAdded;
          onAdded = null;
          done?.();
        }
      } else phaseDone = false;
    }
    render();
    if (!(hoverDone && phaseDone)) frameId = requestAnimationFrame(frame);
    else last = 0;
  };
  const kick = () => {
    if (!frameId) frameId = requestAnimationFrame(frame);
  };

  const setHover = (on: boolean) => {
    if (phase !== 'idle') return;
    const target = on ? 1 : 0;
    if (target === hoverTarget) return;
    hoverTarget = target;
    hoverFrom = hover;
    hoverStart = clock;
    kick();
  };

  const remove = () => {
    if (phase !== 'idle') return Promise.resolve(false);
    phase = 'removing';
    phaseStart = clock;
    // Keep the grabbed pose while it leaves.
    hoverTarget = 1;
    kick();
    return new Promise<boolean>((resolve) => {
      onRemoved = () => resolve(true);
    });
  };

  const add = () => {
    if (phase !== 'removed') return Promise.resolve(false);
    phase = 'adding';
    phaseStart = clock;
    hoverTarget = 0;
    hover = 0;
    hoverVelocity = 0;
    kick();
    return new Promise<boolean>((resolve) => {
      onAdded = () => resolve(true);
    });
  };

  const reset = () => {
    phase = 'idle';
    hoverTarget = 0;
    hover = 0;
    hoverVelocity = 0;
    kick();
  };

  render();

  return {
    svg,
    setHover,
    remove,
    add,
    reset,
    getPhase: () => phase,
    destroy: () => {
      if (frameId) cancelAnimationFrame(frameId);
      svg.remove();
    },
  };
};
